<template>
    <div
        ref="container"
        class="chart-container"
    >
        <v-chart
            v-if="!chartLoading && !emptyRows && !numericAttribute"
            ref="chart"
            autoresize
            :style="{ height: `${windowHeight * 0.39}px` }"
            :option="barChartOptions"
            class="chart"
        />
        <v-chart
            v-else-if="!chartLoading && !emptyRows && numericAttribute"
            :key="numericAttrOptions"
            ref="chart"
            autoresize
            :style="{ height: `${windowHeight * 0.34}px` }"
            :option="numericAttrOptions"
            class="Chart"
        />
        <div
            v-else-if="!chartLoading"
            :style="{ height: `${windowHeight * 0.3}px` }"
            class="flex flex-center"
        >
            <WText>
                {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
            </WText>
        </div>
        <div
            ref="optionsContainer"
            class="OptionsContainer q-pa-xs"
        >
            <div
                v-if="numericAttribute"
                class="column q-pa-xs"
            >
                <div style="display: flex; flex-direction: row; justify-content: space-around;">
                    <div style="display: flex; flex-direction: column;">
                        <div><strong>avg:</strong> {{ stats.avg }}</div>
                        <div><strong>min:</strong> {{ stats.min }}</div>
                    </div>
                    <div style="display: flex; flex-direction: column;">
                        <div><strong>max:</strong> {{ stats.max }}</div>
                        <div><strong>sd:</strong> {{ stats.sd }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import moment from 'moment'
import { dashboardChartsMixin, validatorsMixin } from '@/mixins'
import echartsColors from '@/charts/echartsColors'

export default {
    name: 'AttributeChart',
    mixins: [dashboardChartsMixin, validatorsMixin],
    inject: ['App'],
    props: {
        index: {
            type: [String, Number],
            required: true,
        },
        generalFiltering: {
            type: Object,
            required: true,
        },
        chartData: {
            type: Object,
            required: true,
        },
        chartLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['get-chart-data', 'chartLoading'],

    data() {
        return {
            windowHeight: window.innerHeight - 100,
            emptyRows: false,
            numericAttribute: false,
            stats: { // Stats for Numeric Attributes
                avg: undefined,
                min: undefined,
                max: undefined,
                sd: undefined,
            },
            viewMode: 'ABSOLUTE', // Selected ViewMode

            ownFilters: [], // Each individual graph filters,

            barChartOptions: { // Data for draw Categorical Attributes
                title: {
                    text: '',
                },
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                },
                xAxis: {
                    type: 'category',
                    data: [],
                    axisLabel: {},
                },
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => `${this.formatNumber(value)}${this.range}`,
                        },
                    },
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => this.formatNumber(value),
                        },
                    },
                ],
                grid: {
                    left: 45,
                    right: 45,
                    bottom: 20,
                    top: 20,
                },
                series: [
                    {
                        name: this.$t('visualization.customPlotPanels.duration'),
                        type: 'line',
                        data: [],
                        itemStyle: {
                            color: echartsColors.line.stroke,
                        },
                        yAxisIndex: 0,
                    },
                    {
                        name: this.$t('visualization.customPlotPanels.frequency'),
                        type: 'bar',
                        data: [],
                        itemStyle: {
                            color: echartsColors.bar.fill,
                        },
                        yAxisIndex: 1,
                    },
                ],
            },
            numericAttrOptions: { // Data for draw Categorical Attributes
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                },
                title: {
                    left: 'left',
                    top: '5%',
                    textStyle: {
                        color: echartsColors.series.tertiary,
                        fontSize: '1em',
                    },
                },
                xAxis: {
                    type: 'category',
                    data: [],
                    axisLabel: {},
                },
                yAxis: {
                    type: 'value',
                },
                grid: {
                    left: 45,
                    right: 15,
                    bottom: 20,
                    top: 20,
                },
                series: {
                    name: this.$t('visualization.customPlotPanels.frequency'),
                    type: 'bar',
                    data: [],
                    itemStyle: {
                        color: echartsColors.bar.fill,
                    },
                },
            },
        }
    },

    watch: {
        chartData: {
            handler(newVal) {
                const isCategoricalEmpty = newVal.categoricalRows === null || newVal.categoricalRows.length === 0
                this.emptyRows = isCategoricalEmpty && newVal.numericalRows === null
                if (newVal.categoricalRows === null) {
                    this.updateNumericChart(newVal.numericalRows, newVal.data)
                } else {
                    this.updateCategoricalChart(newVal.categoricalRows)
                }
            },
            deep: true,
        },
    },

    created() {
        this.$eventBus.on(`updateAttribute-${this.index}`, this.updateSelection)
    },

    methods: {

        updateSelection(newVal) {
            const { index, ...rest } = newVal
            this.numericAttribute = rest.numericAttribute || this.numericAttribute
            this.viewMode = rest.viewMode || this.viewMode
            this.getData(rest)
        },

        resetChart() {
            this.numericAttrOptions.series.data = []
            this.numericAttrOptions.xAxis.data = []
            this.barChartOptions.series[1].data = []
            this.barChartOptions.series[0].data = []
            this.barChartOptions.series[1].name = this.$t('visualization.customPlotPanels.frequency')
            this.barChartOptions.series[0].name = this.$t('visualization.customPlotPanels.duration')
            this.barChartOptions.xAxis.data = []
        },

        getData(data) {
            this.$emit('chartLoading', true)
            this.resetChart()
            const params = {
                attribute: data.attribute,
                ...(data.eventAttribute ? data.activities : {}),
                numericAttribute: data.numericAttribute,
                generalFilters: this.generalFiltering.generalFilters || [],
                dashboardFilters: this.generalFiltering.dashboardFilters || [],
                ownFilters: this.generalFiltering.ownFilters || [],
                filterSets: this.generalFiltering ? this.generalFiltering.filterSets : [],
                operator: this.generalFiltering.operator,
                ...(data.numericAttribute
                    ? {
                        startValue: data.startValue,
                        endValue: data.endValue,
                    }
                    : {
                        limit: data.limit,
                        start: data.start,
                        sortBy: data.sortBy.value ? data.sortBy.value : data.sortBy,
                    }
                ),
            }
            this.$emit('get-chart-data', params)
            this.$emit('chartLoading', false)
        },

        updateCategoricalChart(rows) {
            this.numericAttribute = false
            this.barChartOptions.yAxis[1].axisLabel = { show: true }
            this.barChartOptions.yAxis[0].axisLabel = { formatter: (value) => `${this.formatNumber(value)}${this.range}` }
            this.barChartOptions.xAxis.axisLabel = {
                formatter: (value) => {
                    const maxLength = 8
                    // eslint-disable-next-line no-restricted-globals
                    if (!isNaN(value) && typeof value === 'number') {
                        return this.formatNumber(value)
                    }
                    if (value.length > maxLength) {
                        return `${value.slice(0, maxLength)}...`
                    }
                    return `${value}`
                },
            }
            this.barChartOptions.tooltip = {
                trigger: 'axis',
                confine: true,
                formatter: (params) => {
                    let tooltipContent = '<div style="max-width: 15em;">'
                    tooltipContent += `${this.lineBreak(params[0].name)}`
                    params.forEach((param) => {
                        const { seriesName } = param
                        const { value } = param
                        const { color } = param
                        // eslint-disable-next-line max-len
                        tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                               </div>`
                        if (seriesName === this.$t('visualization.customPlotPanels.duration')) {
                            tooltipContent += `${seriesName}: ${moment.duration(value, this.maxDur).format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false })} <br>`
                        } else {
                            tooltipContent += `${seriesName}: ${value} <br>`
                        }
                        tooltipContent += '</div>'
                    })
                    return tooltipContent
                },
            }
            if (this.viewMode === 'ABSOLUTE') {
                const frequencyArr = [rows.map((item) => item.frequency)][0]
                this.barChartOptions.series[1].data = frequencyArr
                this.barChartOptions.series[1].name += ' (abs)'
            } else {
                const percentageArr = [rows.map((item) => item.percentage)][0]
                this.barChartOptions.series[1].data = percentageArr
                this.barChartOptions.series[1].name += ' (%)'
            }
            this.maxDur = this.rangeDuration(Math.max.apply(null, [rows.map((item) => (item.duration))][0]))
            const durationArr = rows.map((item) => this.formatSeconds(item.duration, this.maxDur))
            const commonAttributes = [rows.map((item) => item.attribute)][0]
            this.barChartOptions.series[0].data = durationArr
            this.barChartOptions.xAxis.data = commonAttributes
        },
        updateNumericChart(rows, data) {
            this.numericAttribute = true
            if (data) {
                const dataRows = rows.map((item) => ({
                    value: item.frequency,
                }))
                this.numericAttrOptions.series.data = dataRows
                const rangosConvertidos = rows.map((elemento) => `${elemento.start} - ${elemento.finish}`)
                this.numericAttrOptions.xAxis.data = rangosConvertidos
                this.stats.avg = this.App.numberLocationFormat(this.formatNumber(data.avg), false, true)
                this.stats.max = this.App.numberLocationFormat(this.formatNumber(data.max), false, true)
                this.stats.min = this.App.numberLocationFormat(this.formatNumber(data.min), false, true)
                this.stats.sd = this.App.numberLocationFormat(this.formatNumber(data.sd), false, true)
            }
        },
        handleResize() {
            this.$nextTick(() => {
                if (this.$refs.chart) {
                    this.$refs.chart.resize()
                }
            })
        },
    },
}
</script>
<style lang="scss">
.chart-container {
  margin: 0 auto;
  .chart {
      width: 100%;
      min-width: 100%;
      flex-grow: 1;
      margin-bottom:5px;
      & + .chart {
        width: 100%;
        min-width: 100%;
        @media screen and (max-width: $lg) {
              margin-left: 0px;
          }
      }
  }
}
.attrSelect {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

</style>
